import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { MTMFileDownload } from './mtm-file-download';
import { TransferStatus } from './transfer-status';
import { listenerDownloadStatusChanged, listenerFileDownloadCompleted } from './listeners';
import { HttpHelperService } from '../http-helper.service';

const downloadProgressThrottle: number = 5000;

@Injectable({
  providedIn: 'root',
})
export class SignedUrlDownloader {
  constructor(
    private readonly http: HttpClient,
    private readonly httpHelperService: HttpHelperService,
  ) {}

  /**
   * Initiates file download process
   * @param file - The file to download
   */
  download(file: MTMFileDownload) {
    this.directDownload(file);
  }

  /**
   * Cancels an active download and cleans up resources
   * @param file - The file download to cancel
   */
  cancelDownload(file: MTMFileDownload) {
    file.status = TransferStatus.CANCELED;
    file.buffer = new Uint8Array(0);
    file.progress$.complete();
    file.done$.complete();
    listenerDownloadStatusChanged.next(file);
  }

  /**
   * Performs the actual HTTP download with progress tracking
   * @param file - The file to download
   */
  private directDownload(file: MTMFileDownload) {
    const headers: Record<string, string> = {};
    const httpHeaders = this.httpHelperService.getHttpHeaders();

    httpHeaders.keys().forEach((name) => {
      const values = httpHeaders.getAll(name);
      if (values?.length && name && name !== 'Content-Type') {
        headers[name] = values[0];
      }
    });

    const { url } = file;
    file.currentRequest = this.http
      .get(url, {
        headers: {
          ...headers,
        },
        observe: 'events',
        responseType: 'arraybuffer',
        reportProgress: true,
      })
      .subscribe({
        next: (response: HttpEvent<ArrayBuffer>) => {
          const curTimestamp = new Date().valueOf();
          let newPercentage = 0;
          switch (response.type) {
            case HttpEventType.DownloadProgress:
              if (curTimestamp - file.lastProgressTime < downloadProgressThrottle) {
                return;
              }
              newPercentage = Math.floor((100 * response.loaded) / response.total);
              if (newPercentage > 100) {
                newPercentage = 100;
              }

              if (newPercentage == file.percentage) {
                return;
              }
              file.status = TransferStatus.DOWNLOADING;

              file.percentage = newPercentage;
              if (file.percentage > 0) {
                this.calculateRemainingTime(file);
                listenerDownloadStatusChanged.next(file);
                file.lastProgressTime = curTimestamp;
              }
              break;
            case HttpEventType.Response:
              file.status = TransferStatus.COMPLETED;
              file.percentage = 100;
              file.buffer = new Uint8Array(response.body);
              file.done$.next(file);
              file.done$.complete();
              listenerDownloadStatusChanged.next(file);
              listenerFileDownloadCompleted.next(file);
              break;
          }
        },
        error: (error: Error) => {
          console.log('error downloading chunk', error);
          file.status = TransferStatus.ERROR;
          listenerDownloadStatusChanged.next(file);
        },
      });
  }

  /**
   * Calculates estimated remaining download time based on current progress
   * @param file - The file being downloaded
   */
  private calculateRemainingTime(file: MTMFileDownload) {
    const elapsedTime = Date.now() - file.startDate;
    const expectedDuration = (100 / file.percentage) * elapsedTime;
    file.extraInfo.remainingTime = expectedDuration - elapsedTime;
  }
}
